import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useFormContext } from 'react-hook-form';
import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import type { IncidentPosition } from 'hooks/incident/useRoster';

import type {
  CreateIncidentFormValues,
  DraftRosterAssignment,
  DraftRosterSeat,
} from './createIncidentModel';
import {
  addDraftAssignments,
  addDraftPosition,
  addDraftRootSeat,
  addMissingTemplateNodes,
  assignmentsKeepingCreatorOnIc,
  assignmentsToRosterEntries,
  attachNodesUnderParent,
  deleteDraftSeat,
  dropAssignmentsForSeat,
  peopleFromAssignments,
  removeDraftAssignment,
  removeDraftMember,
  seatsFromTemplateNodes,
  seatsToIncidentPositions,
  setDraftSeatCategory,
  updateDraftSeat,
  type TemplateNode,
} from './draftRosterModel';

export type DraftAddMemberInput = {
  positionId: string;
  user_guid: string;
  name: string;
  email_address: string;
  isLeader?: boolean;
};

export type DraftCreator = {
  user_guid: string;
  name: string;
  email_address: string;
};

type CreateRosterDraftContextValue = {
  isCreateMode: true;
  /** True when the chart is a template: no people, just seats. */
  structureOnly: boolean;
  groupGuid: string;
  positions: IncidentPosition[];
  members: RosterEntry[];
  addMembers: (incoming: DraftAddMemberInput[]) => void;
  removeMemberFromPosition: (userGuid: string, positionId: string) => void;
  removeMember: (userGuid: string) => void;
  createSeat: (input: {
    name: string;
    reportsTo?: string;
    isWorkgroup?: boolean;
  }) => void;
  editSeat: (input: {
    id: string;
    name: string;
    reportsTo?: string | null;
    isWorkgroup?: boolean;
  }) => void;
  deleteSeat: (id: string) => void;
  setSeatCategory: (
    id: string,
    category: string,
    customCategory?: string | null
  ) => void;
  applyTemplate: (input: {
    nodes: TemplateNode[];
    mode: 'add' | 'replace';
    slug: string;
    name: string;
  }) => { created: { id: string; name: string }[] };
  /**
   * Replaces the draft chart with a structure and seats the named users on it
   * in one write, so the assignments are not applied against the previous
   * tree.
   */
  applyFilledRoster: (input: {
    nodes: TemplateNode[];
    slug: string;
    name: string;
    people: {
      seatName: string;
      user_guid: string;
      name: string;
      email_address: string;
    }[];
  }) => void;
  attachExpansion: (input: {
    nodes: TemplateNode[];
    parentPositionId: string;
  }) => { created: { id: string; name: string }[] };
  addCatalogPosition: (input: {
    name: string;
    parentPositionId: string;
    isWorkgroup?: boolean;
  }) => void;
};

const CreateRosterDraftContext =
  createContext<CreateRosterDraftContextValue | null>(null);

export function useOptionalCreateRosterDraft() {
  return useContext(CreateRosterDraftContext);
}

export function useCreateRosterDraft() {
  const value = useOptionalCreateRosterDraft();
  if (!value) {
    throw new Error(
      'useCreateRosterDraft must be used within CreateRosterDraftProvider'
    );
  }
  return value;
}

type RosterDraftProviderProps = {
  groupGuid: string;
  seats: DraftRosterSeat[];
  assignments: DraftRosterAssignment[];
  creator?: DraftCreator | null;
  structureOnly?: boolean;
  onChange: (
    nextSeats: DraftRosterSeat[],
    nextAssignments: DraftRosterAssignment[]
  ) => void;
  onTemplateApplied?: (slug: string, name: string) => void;
  /** Called whenever a template is loaded, in either add or replace mode. */
  onTemplateLoaded?: (slug: string) => void;
  children: ReactNode;
};

/**
 * Controlled draft roster. The create-incident wizard and the Hub template
 * editor both mount this; each binds `onChange` to its own store (form or
 * useState).
 */
export function RosterDraftProvider({
  groupGuid,
  seats,
  assignments,
  creator = null,
  structureOnly = false,
  onChange,
  onTemplateApplied,
  onTemplateLoaded,
  children,
}: RosterDraftProviderProps) {
  const persist = useCallback(
    (
      nextSeats: DraftRosterSeat[],
      nextAssignments: DraftRosterAssignment[] = assignments
    ) => {
      const seated = creator
        ? assignmentsKeepingCreatorOnIc(nextSeats, nextAssignments, creator)
        : nextAssignments;
      onChange(nextSeats, seated);
    },
    [assignments, creator, onChange]
  );

  useEffect(() => {
    if (!creator) return;
    const next = assignmentsKeepingCreatorOnIc(seats, assignments, creator);
    const same =
      next.length === assignments.length &&
      next.every(
        (entry, index) =>
          entry.positionId === assignments[index]?.positionId &&
          entry.user_guid === assignments[index]?.user_guid &&
          !!entry.isLeader === !!assignments[index]?.isLeader
      );
    if (same) return;
    onChange(seats, next);
  }, [assignments, creator, onChange, seats]);

  const positions = useMemo(() => seatsToIncidentPositions(seats), [seats]);
  const members = useMemo(
    () => assignmentsToRosterEntries(seats, assignments, groupGuid),
    [assignments, groupGuid, seats]
  );

  const addMembers = useCallback(
    (incoming: DraftAddMemberInput[]) => {
      persist(seats, addDraftAssignments(assignments, incoming));
    },
    [assignments, persist, seats]
  );

  const removeMemberFromPosition = useCallback(
    (userGuid: string, positionId: string) => {
      persist(seats, removeDraftAssignment(assignments, userGuid, positionId));
    },
    [assignments, persist, seats]
  );

  const removeMember = useCallback(
    (userGuid: string) => {
      persist(seats, removeDraftMember(assignments, userGuid));
    },
    [assignments, persist, seats]
  );

  const createSeat = useCallback(
    ({
      name,
      reportsTo,
      isWorkgroup,
    }: {
      name: string;
      reportsTo?: string;
      isWorkgroup?: boolean;
    }) => {
      const parentId =
        reportsTo ??
        seats.find((seat) => seat.parentId === null)?.id ??
        seats[0]?.id;
      if (!parentId) {
        persist(addDraftRootSeat(seats, name, isWorkgroup));
        return;
      }
      persist(addDraftPosition(seats, parentId, name, isWorkgroup));
    },
    [persist, seats]
  );

  const editSeat = useCallback(
    ({
      id,
      name,
      reportsTo,
      isWorkgroup,
    }: {
      id: string;
      name: string;
      reportsTo?: string | null;
      isWorkgroup?: boolean;
    }) => {
      persist(
        updateDraftSeat(seats, id, {
          name,
          parentId: reportsTo,
          isWorkgroup,
        })
      );
    },
    [persist, seats]
  );

  const deleteSeat = useCallback(
    (id: string) => {
      persist(
        deleteDraftSeat(seats, id),
        dropAssignmentsForSeat(assignments, id)
      );
    },
    [assignments, persist, seats]
  );

  const setSeatCategory = useCallback(
    (id: string, category: string, customCategory?: string | null) => {
      persist(setDraftSeatCategory(seats, id, category, customCategory));
    },
    [persist, seats]
  );

  const applyTemplate = useCallback(
    ({
      nodes,
      mode,
      slug,
      name,
    }: {
      nodes: TemplateNode[];
      mode: 'add' | 'replace';
      slug: string;
      name: string;
    }) => {
      if (mode === 'replace') {
        const nextSeats = seatsFromTemplateNodes(nodes);
        persist(nextSeats, [] as DraftRosterAssignment[]);
        onTemplateApplied?.(slug, name);
        onTemplateLoaded?.(slug);
        return {
          created: nextSeats.map((seat) => ({ id: seat.id, name: seat.name })),
        };
      }
      const before = new Set(seats.map((seat) => seat.id));
      const nextSeats = addMissingTemplateNodes(seats, nodes);
      persist(nextSeats);
      onTemplateLoaded?.(slug);
      return {
        created: nextSeats
          .filter((seat) => !before.has(seat.id))
          .map((seat) => ({ id: seat.id, name: seat.name })),
      };
    },
    [onTemplateApplied, onTemplateLoaded, persist, seats]
  );

  const applyFilledRoster = useCallback(
    ({
      nodes,
      slug,
      name,
      people,
    }: {
      nodes: TemplateNode[];
      slug: string;
      name: string;
      people: {
        seatName: string;
        user_guid: string;
        name: string;
        email_address: string;
      }[];
    }) => {
      const nextSeats = seatsFromTemplateNodes(nodes);
      const idByName = new Map(nextSeats.map((seat) => [seat.name, seat.id]));
      const incoming: DraftRosterAssignment[] = [];
      for (const person of people) {
        const positionId = idByName.get(person.seatName);
        if (!positionId || !person.user_guid) continue;
        incoming.push({
          positionId,
          user_guid: person.user_guid,
          name: person.name,
          email_address: person.email_address,
          isLeader: false,
        });
      }
      persist(nextSeats, incoming);
      onTemplateApplied?.(slug, name);
    },
    [onTemplateApplied, persist]
  );

  const attachExpansion = useCallback(
    ({
      nodes,
      parentPositionId,
    }: {
      nodes: TemplateNode[];
      parentPositionId: string;
    }) => {
      const before = new Set(seats.map((seat) => seat.id));
      const nextSeats = attachNodesUnderParent(seats, parentPositionId, nodes);
      persist(nextSeats);
      return {
        created: nextSeats
          .filter((seat) => !before.has(seat.id))
          .map((seat) => ({ id: seat.id, name: seat.name })),
      };
    },
    [persist, seats]
  );

  const addCatalogPosition = useCallback(
    ({
      name,
      parentPositionId,
      isWorkgroup,
    }: {
      name: string;
      parentPositionId: string;
      isWorkgroup?: boolean;
    }) => {
      persist(addDraftPosition(seats, parentPositionId, name, isWorkgroup));
    },
    [persist, seats]
  );

  const value = useMemo(
    (): CreateRosterDraftContextValue => ({
      isCreateMode: true,
      structureOnly,
      groupGuid,
      positions,
      members,
      addMembers,
      removeMemberFromPosition,
      removeMember,
      createSeat,
      editSeat,
      deleteSeat,
      setSeatCategory,
      applyTemplate,
      applyFilledRoster,
      attachExpansion,
      addCatalogPosition,
    }),
    [
      addCatalogPosition,
      addMembers,
      applyFilledRoster,
      applyTemplate,
      attachExpansion,
      createSeat,
      deleteSeat,
      editSeat,
      groupGuid,
      members,
      positions,
      removeMember,
      removeMemberFromPosition,
      setSeatCategory,
      structureOnly,
    ]
  );

  return (
    <CreateRosterDraftContext.Provider value={value}>
      {children}
    </CreateRosterDraftContext.Provider>
  );
}

/**
 * Wizard adapter: binds the controlled draft to react-hook-form so existing
 * create-incident consumers keep working unchanged.
 */
export function CreateRosterDraftProvider({
  groupGuid,
  creator,
  children,
}: {
  groupGuid: string;
  creator: DraftCreator | null;
  children: ReactNode;
}) {
  const { watch, setValue } = useFormContext<CreateIncidentFormValues>();
  const seats = watch('roster_seats');
  const assignments = watch('roster_assignments');

  const handleChange = useCallback(
    (
      nextSeats: DraftRosterSeat[],
      nextAssignments: DraftRosterAssignment[]
    ) => {
      setValue('roster_seats', nextSeats, { shouldDirty: true });
      setValue('roster_assignments', nextAssignments, { shouldDirty: true });
      setValue('people', peopleFromAssignments(nextSeats, nextAssignments), {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const handleTemplateApplied = useCallback(
    (slug: string, name: string) => {
      setValue('roster_template_slug', slug, { shouldDirty: true });
      setValue('roster_template_name', name, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <RosterDraftProvider
      groupGuid={groupGuid}
      seats={seats}
      assignments={assignments}
      creator={creator}
      onChange={handleChange}
      onTemplateApplied={handleTemplateApplied}
    >
      {children}
    </RosterDraftProvider>
  );
}
